import { cn } from '@/utils';
import { useRef } from 'react';

export const LogoOutline = (props: { className?: string; showNew?: boolean }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const isFilled = useRef(false);
  const isAnimating = useRef(false);

  const handleMouseEnter = () => {
    if (!svgRef.current || isAnimating.current) return;

    isAnimating.current = true;

    const visibleAnims = ['visible-draw-1', 'visible-draw-2', 'visible-draw-3', 'visible-draw-4'];
    const maskDrawAnims = ['draw-1', 'draw-2', 'draw-3', 'draw-4'];
    const maskExpandAnims = ['expand-1', 'expand-2', 'expand-3', 'expand-4'];
    const visibleReverse = ['visible-reverse-1', 'visible-reverse-2', 'visible-reverse-3', 'visible-reverse-4'];
    const maskCollapse = ['collapse-1', 'collapse-2', 'collapse-3', 'collapse-4'];
    const maskReverse = ['reverse-1', 'reverse-2', 'reverse-3', 'reverse-4'];

    if (!isFilled.current) {
      // Forward animation (fill)
      isFilled.current = true;

      // Show colored content group
      const showColored = svgRef.current.getElementById('show-colored') as any;
      if (showColored?.beginElement) showColored.beginElement();

      // Start visible colored stroke drawing (draws over black)
      visibleAnims.forEach(id => {
        const elem = svgRef.current?.getElementById(id) as any;
        if (elem?.beginElement) elem.beginElement();
      });

      // Start mask stroke drawing
      maskDrawAnims.forEach(id => {
        const elem = svgRef.current?.getElementById(id) as any;
        if (elem?.beginElement) elem.beginElement();
      });

      // Start mask stroke expansion after 0.6s delay (when drawing completes)
      setTimeout(() => {
        maskExpandAnims.forEach(id => {
          const elem = svgRef.current?.getElementById(id) as any;
          if (elem?.beginElement) elem.beginElement();
        });
        // Reset animation flag after forward animation completes (600ms + 400ms)
        setTimeout(() => {
          isAnimating.current = false;
        }, 400);
      }, 600);
    } else {
      // Reverse animation (unfill)
      isFilled.current = false;

      // Start mask collapse
      maskCollapse.forEach(id => {
        const elem = svgRef.current?.getElementById(id) as any;
        if (elem?.beginElement) elem.beginElement();
      });

      // Start mask reverse after collapse
      setTimeout(() => {
        maskReverse.forEach(id => {
          const elem = svgRef.current?.getElementById(id) as any;
          if (elem?.beginElement) elem.beginElement();
        });

        // Start visible stroke reverse
        visibleReverse.forEach(id => {
          const elem = svgRef.current?.getElementById(id) as any;
          if (elem?.beginElement) elem.beginElement();
        });
      }, 400);

      // Hide colored content group after all animations complete
      setTimeout(() => {
        const hideColored = svgRef.current?.getElementById('hide-colored') as any;
        if (hideColored?.beginElement) hideColored.beginElement();
        // Reset animation flag after reverse animation completes
        isAnimating.current = false;
      }, 1000);
    }
  };

  return (
    <div
      className={cn(
        `flex flex-col gap-1 transition-all duration-1000 ease-in-out cursor-pointer group`,
        props.className
      )}
      onMouseEnter={handleMouseEnter}
    >
      <svg ref={svgRef} viewBox="0 0 917 315" fill="none" xmlns="http://www.w3.org/2000/svg" className="logo-svg">
        <defs>
          {/* Gradients */}
          <linearGradient
            id="logo-gradient-1"
            x1="264.312"
            y1="-3.23545"
            x2="169.514"
            y2="216.42"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#00FFBF" />
            <stop offset="1" stopColor="#2E95FF" />
          </linearGradient>
          <linearGradient
            id="logo-gradient-2"
            x1="264.961"
            y1="143.977"
            x2="168.122"
            y2="365.116"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#00FFBF" />
            <stop offset="1" stopColor="#2E95FF" />
          </linearGradient>
          <linearGradient
            id="logo-gradient-3"
            x1="584.571"
            y1="-5.7857"
            x2="331.906"
            y2="311.112"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FF0090" />
            <stop offset="1" stopColor="#7200DD" />
          </linearGradient>
          <linearGradient
            id="logo-gradient-4"
            x1="907.017"
            y1="-5.90534"
            x2="656.099"
            y2="328.357"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FCAF28" />
            <stop offset="1" stopColor="#FF3C00" />
          </linearGradient>

          {/* Shape definitions */}
          <path
            id="shape-1"
            d="M18.4159 46.6667C31.4292 24.2 56.8159 9.33334 82.8159 9.25334C126.803 9.16 170.789 9.30667 214.776 9.25334C228.992 9.11073 243.218 9.43742 257.438 9.22361C260.933 9.17105 262.894 13.3032 260.606 15.9471C233.07 47.7782 205.96 80.0182 178.283 111.76C165.838 126.449 153.368 141.138 140.35 155.318C139.946 155.759 139.447 156.1 138.888 156.311C138.876 156.316 138.863 156.321 138.851 156.326C138.377 156.504 137.876 156.601 137.37 156.602C120.039 156.637 102.721 156.546 85.4025 156.52C68.2692 156.653 50.8692 151.253 37.6692 140.147C24.2292 130.093 15.2025 115.013 10.8292 98.96C6.88254 81.4267 9.28255 62.2667 18.4159 46.6667Z"
            pathLength="1"
          />
          <path
            id="shape-2"
            d="M138.652 158.364C139.066 157.879 139.588 157.494 140.19 157.282C142.31 156.536 144.543 156.162 146.816 156.267C162.829 156.373 178.856 156.16 194.869 156.453C212.643 157.027 229.683 164.587 242.989 176.187C258.563 190.307 268.136 211.213 267.243 232.373C267.736 263 246.269 292.093 217.336 301.573C208.723 304.133 199.736 305.213 190.789 305.547C133.745 305.395 76.6876 305.399 19.6415 305.434C16.153 305.436 14.3747 301.322 16.799 298.813C19.9471 295.556 23.0461 292.355 26.0294 288.947C56.6961 253.8 86.8961 218.24 117.656 183.173C124.609 174.874 131.611 166.601 138.652 158.364Z"
            pathLength="1"
          />
          <path
            id="shape-3"
            d="M311.666 15.4127C310.41 12.7606 312.342 9.70326 315.277 9.70049C403.565 9.61711 491.864 9.65279 580.151 9.70308C583.064 9.70474 584.997 12.7182 583.773 15.361C569.658 45.8496 554.488 75.8476 540.536 106.413C515.536 157.973 491.629 210.08 466.896 261.787C461.959 272.953 456.398 283.853 451.371 294.976C449.939 298.144 445.386 298.222 443.917 295.07C400.333 201.636 355.831 108.598 311.666 15.4127Z"
            pathLength="1"
          />
          <path
            id="shape-4"
            d="M678.776 33.8533C703.176 17.96 732.229 8.88 761.443 9.62667C787.349 9.76 813.083 17.2133 835.323 30.4533C842.557 34.7601 848.148 38.8588 856.944 47.0289C858.536 48.5074 858.623 50.9925 857.15 52.5893C846.753 63.86 836.979 73.5009 826.883 84.52C807.119 106.597 787.188 128.508 767.599 150.739C765.368 153.271 767.092 157.237 770.466 157.308C792.315 157.763 814.189 157.246 836.043 157.4C858.798 157.337 881.553 157.607 904.32 157.446C906.52 157.43 908.33 159.19 908.298 161.39C907.67 205.071 885.712 247.562 851.069 274.027C843.349 280.373 834.709 285.347 825.976 290.133C783.509 311.64 730.469 310.8 688.883 287.493C672.856 279.053 658.496 267.507 647.003 253.493C613.163 215.573 602.189 159.147 619.016 111.24C629.269 79.5467 651.149 52.2133 678.776 33.8533Z"
            pathLength="1"
          />

          {/* Masks for outline-to-fill animation */}
          <mask id="fill-mask-1">
            <rect width="100%" height="100%" fill="black" />
            <use href="#shape-1" fill="none" stroke="white" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-dasharray" begin="indefinite" id="draw-1" from="0 1" to="1 0" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
              <animate attributeName="stroke-dasharray" begin="indefinite" id="reverse-1" from="1 0" to="0 1" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
            </use>
            <use href="#shape-1" fill="none" stroke="white" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-width" begin="indefinite" id="expand-1" dur="0.4s" from="17" to="400" fill="freeze" calcMode="spline" keySplines="0.2 0 0.2 1" />
              <animate attributeName="stroke-width" begin="indefinite" id="collapse-1" dur="0.4s" from="400" to="17" fill="freeze" calcMode="spline" keySplines="0.4 0 0.8 1" />
            </use>
          </mask>
          <mask id="fill-mask-2">
            <rect width="100%" height="100%" fill="black" />
            <use href="#shape-2" fill="none" stroke="white" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-dasharray" begin="indefinite" id="draw-2" from="0 1" to="1 0" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
              <animate attributeName="stroke-dasharray" begin="indefinite" id="reverse-2" from="1 0" to="0 1" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
            </use>
            <use href="#shape-2" fill="none" stroke="white" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-width" begin="indefinite" id="expand-2" dur="0.4s" from="17" to="400" fill="freeze" calcMode="spline" keySplines="0.2 0 0.2 1" />
              <animate attributeName="stroke-width" begin="indefinite" id="collapse-2" dur="0.4s" from="400" to="17" fill="freeze" calcMode="spline" keySplines="0.4 0 0.8 1" />
            </use>
          </mask>
          <mask id="fill-mask-3">
            <rect width="100%" height="100%" fill="black" />
            <use href="#shape-3" fill="none" stroke="white" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-dasharray" begin="indefinite" id="draw-3" from="0 1" to="1 0" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
              <animate attributeName="stroke-dasharray" begin="indefinite" id="reverse-3" from="1 0" to="0 1" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
            </use>
            <use href="#shape-3" fill="none" stroke="white" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-width" begin="indefinite" id="expand-3" dur="0.4s" from="17" to="400" fill="freeze" calcMode="spline" keySplines="0.2 0 0.2 1" />
              <animate attributeName="stroke-width" begin="indefinite" id="collapse-3" dur="0.4s" from="400" to="17" fill="freeze" calcMode="spline" keySplines="0.4 0 0.8 1" />
            </use>
          </mask>
          <mask id="fill-mask-4">
            <rect width="100%" height="100%" fill="black" />
            <use href="#shape-4" fill="none" stroke="white" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-dasharray" begin="indefinite" id="draw-4" from="0 1" to="1 0" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
              <animate attributeName="stroke-dasharray" begin="indefinite" id="reverse-4" from="1 0" to="0 1" dur="0.5s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
            </use>
            <use href="#shape-4" fill="none" stroke="white" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="stroke-width" begin="indefinite" id="expand-4" dur="0.4s" from="17" to="400" fill="freeze" calcMode="spline" keySplines="0.2 0 0.2 1" />
              <animate attributeName="stroke-width" begin="indefinite" id="collapse-4" dur="0.4s" from="400" to="17" fill="freeze" calcMode="spline" keySplines="0.4 0 0.8 1" />
            </use>
          </mask>
        </defs>

        {/* Initial black outline strokes (always visible) */}
        <use href="#shape-1" fill="none" stroke="currentColor" strokeWidth="17" />
        <use href="#shape-2" fill="none" stroke="currentColor" strokeWidth="17" />
        <use href="#shape-3" fill="none" stroke="currentColor" strokeWidth="17" />
        <use href="#shape-4" fill="none" stroke="currentColor" strokeWidth="17" />

        {/* Colored content (only visible on hover) */}
        <g opacity="0">
          <animate attributeName="opacity" begin="indefinite" id="show-colored" from="0" to="1" dur="0.01s" fill="freeze" />
          <animate attributeName="opacity" begin="indefinite" id="hide-colored" from="1" to="0" dur="0.01s" fill="freeze" />

          {/* Colored stroke drawing (draws over black on hover) */}
          <use href="#shape-1" fill="none" stroke="url(#logo-gradient-1)" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-draw-1" from="0 1" to="1 0" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-reverse-1" from="1 0" to="0 1" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
          </use>
          <use href="#shape-2" fill="none" stroke="url(#logo-gradient-2)" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-draw-2" from="0 1" to="1 0" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-reverse-2" from="1 0" to="0 1" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
          </use>
          <use href="#shape-3" fill="none" stroke="url(#logo-gradient-3)" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-draw-3" from="0 1" to="1 0" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-reverse-3" from="1 0" to="0 1" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
          </use>
          <use href="#shape-4" fill="none" stroke="url(#logo-gradient-4)" strokeWidth="17" strokeDasharray="0 1" strokeLinecap="round" strokeLinejoin="round">
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-draw-4" from="0 1" to="1 0" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1" />
            <animate attributeName="stroke-dasharray" begin="indefinite" id="visible-reverse-4" from="1 0" to="0 1" dur="0.6s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.6 1" />
          </use>

          {/* Colored fill revealed by mask */}
          <use href="#shape-1" fill="url(#logo-gradient-1)" mask="url(#fill-mask-1)" />
          <use href="#shape-2" fill="url(#logo-gradient-2)" mask="url(#fill-mask-2)" />
          <use href="#shape-3" fill="url(#logo-gradient-3)" mask="url(#fill-mask-3)" />
          <use href="#shape-4" fill="url(#logo-gradient-4)" mask="url(#fill-mask-4)" />
        </g>
      </svg>
      {props.showNew && <div className="font-medium">.new</div>}
    </div>
  );
};

export const LogoColor = (props: { className?: string; showNew?: boolean }) => {
  return (
    <div
      className={cn(
        `flex flex-col gap-1 transition-all duration-1000 ease-in-out cursor-pointer`,
        props.className
      )}
    >
      <svg viewBox="0 0 900 297" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M305.371 12.1182C302.857 6.81452 306.721 0.699863 312.591 0.694693C396.7 0.620613 480.818 0.65233 564.927 0.699533C570.752 0.702802 574.615 6.72832 572.147 12.0042C558.789 40.5568 544.626 68.7352 531.536 97.4133C506.536 148.973 482.629 201.08 457.896 252.787C454.182 261.187 450.114 269.437 446.187 277.745C443.214 284.032 434.023 284.151 431.079 277.85C389.614 189.085 347.352 100.663 305.371 12.1182Z"
          fill="url(#paint0_linear_546_2)"
        />
        <path
          d="M669.776 24.8533C694.176 8.96001 723.229 -0.119965 752.443 0.626702C778.349 0.760035 804.083 8.21336 826.323 21.4534C832.734 25.2699 837.854 28.9231 845.058 35.3909C848.291 38.2937 848.459 43.2772 845.492 46.4511C836.077 56.5215 827.115 65.4446 817.883 75.52C799.995 95.5013 781.971 115.346 764.187 135.416C759.712 140.466 763.156 148.367 769.904 148.439C788.946 148.641 808.002 148.266 827.043 148.4C848.473 148.341 869.904 148.577 891.344 148.47C895.744 148.448 899.375 151.969 899.182 156.364C897.325 198.637 875.661 239.366 842.069 265.027C834.349 271.373 825.709 276.347 816.976 281.133C774.509 302.64 721.469 301.8 679.883 278.493C663.856 270.053 649.496 258.507 638.003 244.493C604.163 206.573 593.189 150.147 610.016 102.24C620.269 70.5467 642.149 43.2133 669.776 24.8533Z"
          fill="url(#paint1_linear_546_2)"
        />
        <path
          d="M9.41588 37.6667C22.4292 15.2 47.8159 0.333337 73.8159 0.253337C117.803 0.160003 161.789 0.30667 205.776 0.253337C216.929 0.141465 228.087 0.318407 239.244 0.296642C246.236 0.283001 250.162 8.61007 245.598 13.9065C220.12 43.4706 194.945 73.3294 169.283 102.76C156.843 117.444 144.377 132.127 131.365 146.302C130.951 146.753 130.432 147.106 129.859 147.322V147.322C129.375 147.505 128.87 147.601 128.352 147.602C111.027 147.637 93.7148 147.546 76.4025 147.52C59.2692 147.653 41.8692 142.253 28.6692 131.147C15.2292 121.093 6.20254 106.013 1.82921 89.96C-2.11746 72.4267 0.282548 53.2667 9.41588 37.6667Z"
          fill="url(#paint2_linear_546_2)"
        />
        <path
          d="M129.022 150.102C129.85 149.132 130.89 148.345 132.113 147.983C133.954 147.438 135.87 147.177 137.816 147.267C153.829 147.373 169.856 147.16 185.869 147.453C203.643 148.027 220.683 155.587 233.989 167.187C249.563 181.307 259.136 202.213 258.243 223.373C258.736 254 237.269 283.093 208.336 292.573C199.723 295.133 190.736 296.213 181.789 296.547C127.793 296.403 73.7856 296.399 19.7869 296.429C12.81 296.433 9.37132 288.316 14.1063 283.191C15.0912 282.126 16.0664 281.047 17.0294 279.947C47.6961 244.8 77.8961 209.24 108.656 174.173C115.401 166.122 122.193 158.095 129.022 150.102Z"
          fill="url(#paint3_linear_546_2)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_546_2"
            x1="584.571"
            y1="-5.7857"
            x2="331.906"
            y2="311.112"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FF0090" />
            <stop offset="1" stopColor="#7200DD" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_546_2"
            x1="907.017"
            y1="-5.90534"
            x2="656.099"
            y2="328.357"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FCAF28" />
            <stop offset="1" stopColor="#FF3C00" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_546_2"
            x1="264.312"
            y1="-3.23545"
            x2="169.514"
            y2="216.42"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#00FFBF" />
            <stop offset="1" stopColor="#2E95FF" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_546_2"
            x1="264.961"
            y1="143.977"
            x2="168.122"
            y2="365.116"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#00FFBF" />
            <stop offset="1" stopColor="#2E95FF" />
          </linearGradient>
        </defs>
      </svg>

      {props.showNew && <div className="font-medium">.new</div>}
    </div>
  );
};

export const LogoSolid = (props: { className?: string; showNew?: boolean }) => {
  return (
    <div
      className={cn(
        `flex flex-col gap-1 transition-all duration-1000 ease-in-out cursor-pointer`,
        props.className
      )}
    >
      <svg viewBox="0 0 901 297" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M10.4159 37.6667C23.4292 15.2 48.8159 0.333337 74.8159 0.253337C118.803 0.160003 162.789 0.30667 206.776 0.253337C220.992 0.110732 235.218 0.437417 249.438 0.223612C252.933 0.171053 254.894 4.30317 252.606 6.9471C225.07 38.7782 197.96 71.0182 170.283 102.76C157.838 117.449 145.368 132.138 132.35 146.318C131.946 146.759 131.447 147.1 130.888 147.311C130.876 147.316 130.863 147.321 130.851 147.326C130.377 147.504 129.876 147.601 129.37 147.602C112.039 147.637 94.7209 147.546 77.4025 147.52C60.2692 147.653 42.8692 142.253 29.6692 131.147C16.2292 121.093 7.20254 106.013 2.82921 89.96C-1.11746 72.4267 1.28255 53.2667 10.4159 37.6667Z"
          fill="currentColor"
        />
        <path
          d="M303.666 6.41263C302.41 3.76059 304.342 0.703226 307.277 0.700455C395.565 0.617076 483.864 0.65276 572.151 0.703054C575.064 0.704713 576.997 3.71816 575.773 6.36098C561.658 36.8495 546.488 66.8476 532.536 97.4133C507.536 148.973 483.629 201.08 458.896 252.787C453.959 263.953 448.398 274.853 443.371 285.976C441.939 289.144 437.386 289.222 435.917 286.07C392.333 192.636 347.831 99.5976 303.666 6.41263Z"
          fill="currentColor"
        />
        <path
          d="M670.776 24.8533C695.176 8.96001 724.229 -0.119965 753.443 0.626702C779.349 0.760035 805.083 8.21336 827.323 21.4534C834.557 25.7601 840.148 29.8589 848.944 38.0289C850.536 39.5074 850.623 41.9925 849.15 43.5894C838.753 54.86 828.979 64.5009 818.883 75.52C799.119 97.5975 779.188 119.508 759.599 141.739C757.368 144.271 759.092 148.237 762.466 148.308C784.315 148.763 806.189 148.246 828.043 148.4C850.798 148.337 873.553 148.607 896.32 148.446C898.52 148.43 900.33 150.19 900.298 152.39C899.67 196.072 877.712 238.562 843.069 265.027C835.349 271.373 826.709 276.347 817.976 281.133C775.509 302.64 722.469 301.8 680.883 278.493C664.856 270.053 650.496 258.507 639.003 244.493C605.163 206.573 594.189 150.147 611.016 102.24C621.269 70.5467 643.149 43.2133 670.776 24.8533Z"
          fill="currentColor"
        />
        <path
          d="M130.652 149.364C131.066 148.879 131.588 148.494 132.19 148.282C134.31 147.536 136.543 147.162 138.816 147.267C154.829 147.373 170.856 147.16 186.869 147.453C204.643 148.027 221.683 155.587 234.989 167.187C250.563 181.307 260.136 202.213 259.243 223.373C259.736 254 238.269 283.093 209.336 292.573C200.723 295.133 191.736 296.213 182.789 296.547C125.744 296.395 68.6875 296.399 11.6414 296.434C8.1529 296.436 6.3746 292.322 8.79883 289.813C11.9469 286.556 15.046 283.355 18.0293 279.947C48.696 244.8 78.896 209.24 109.656 174.173C116.608 165.874 123.611 157.601 130.652 149.364Z"
          fill="currentColor"
        />
      </svg>

      {props.showNew && <div className="font-medium">.new</div>}
    </div>
  );
};
